import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

const DEFAULT_FORM = {
  ambiente: 'sandbox',
  api_base_url: '',
  ativo: true,
  api_key: '',
  webhook_token: '',
  clear_api_key: false,
  clear_webhook_token: false,
};

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('pt-BR');
}

function toFormFromSetting(setting) {
  return {
    ambiente: setting?.ambiente || 'sandbox',
    api_base_url: setting?.api_base_url || '',
    ativo: setting?.ativo !== false,
    api_key: '',
    webhook_token: '',
    clear_api_key: false,
    clear_webhook_token: false,
  };
}

const SolarSettingsPage = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [asaasSetting, setAsaasSetting] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);

  const mappingSummary = useMemo(() => {
    const total = mappings.length;
    const active = mappings.filter((item) => item.ativo !== false).length;
    const inactive = Math.max(0, total - active);
    return { total, active, inactive };
  }, [mappings]);

  const loadPage = async () => {
    setLoading(true);
    try {
      const [mappingsRs, asaasRs] = await Promise.all([
        axios.get('/api/solar/enel-import-mappings', { params: { include_inactive: '1' } }),
        axios.get('/api/solar/settings/asaas'),
      ]);

      const nextMappings = Array.isArray(mappingsRs.data?.items) ? mappingsRs.data.items : [];
      const nextAsaas = asaasRs.data?.item || null;
      setMappings(nextMappings);
      setAsaasSetting(nextAsaas);
      setForm(toFormFromSetting(nextAsaas));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar configuracoes do modulo solar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPage();
  }, []);

  const saveAsaas = async () => {
    const payload = {
      ambiente: form.ambiente,
      api_base_url: String(form.api_base_url || '').trim() || null,
      ativo: form.ativo === true,
      clear_api_key: form.clear_api_key === true,
      clear_webhook_token: form.clear_webhook_token === true,
    };

    const apiKey = String(form.api_key || '').trim();
    const webhookToken = String(form.webhook_token || '').trim();
    if (apiKey) payload.api_key = apiKey;
    if (webhookToken) payload.webhook_token = webhookToken;

    setSaving(true);
    try {
      const { data } = await axios.put('/api/solar/settings/asaas', payload);
      const next = data?.item || null;
      setAsaasSetting(next);
      setForm(toFormFromSetting(next));
      toast.success('Configuracoes da Asaas salvas');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar configuracoes da Asaas');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Solar · Configuracoes</h1>
          <p className="text-gray-600">Central de configuracoes especificas do modulo Solar</p>
        </div>
        {loading && <div className="loader h-6 w-6" />}
      </div>

      <div className="card">
        <div className="card-header">1. Mapeamento de Importacao ENEL</div>
        <div className="card-body grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs text-gray-500">Total de Regras</p>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{mappingSummary.total}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs text-gray-500">Regras Ativas</p>
            <p className="text-2xl font-semibold text-green-700 mt-1">{mappingSummary.active}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs text-gray-500">Regras Inativas</p>
            <p className="text-2xl font-semibold text-amber-700 mt-1">{mappingSummary.inactive}</p>
          </div>
          <div className="lg:col-span-3 flex flex-wrap gap-3">
            <Link to="/solar/mapeamentos-enel" className="btn-primary inline-flex items-center">
              Gerenciar Mapeamentos ENEL
              <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-2" />
            </Link>
            <button className="btn-secondary" onClick={loadPage} disabled={loading}>
              Atualizar Resumo
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">2. Integracao Asaas (por Tenant/Empresa)</div>
        <div className="card-body space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <p className="text-xs text-gray-500">API Key</p>
              <p className="text-sm text-gray-900 mt-1">
                {asaasSetting?.has_api_key ? `Configurada (${asaasSetting.api_key_masked || '***'})` : 'Nao configurada'}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <p className="text-xs text-gray-500">Webhook Token</p>
              <p className="text-sm text-gray-900 mt-1">
                {asaasSetting?.has_webhook_token
                  ? `Configurado (${asaasSetting.webhook_token_masked || '***'})`
                  : 'Nao configurado'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Ambiente</label>
              <select
                className="form-select"
                value={form.ambiente}
                onChange={(e) => setForm((old) => ({ ...old, ambiente: e.target.value }))}
              >
                <option value="sandbox">Sandbox</option>
                <option value="production">Producao</option>
              </select>
            </div>
            <div>
              <label className="form-label">API Base URL (opcional)</label>
              <input
                className="form-input"
                value={form.api_base_url}
                onChange={(e) => setForm((old) => ({ ...old, api_base_url: e.target.value }))}
                placeholder="https://api-sandbox.asaas.com/v3"
              />
            </div>
            <div>
              <label className="form-label">Nova API Key (opcional)</label>
              <input
                type="password"
                className="form-input"
                value={form.api_key}
                onChange={(e) => setForm((old) => ({ ...old, api_key: e.target.value, clear_api_key: false }))}
                placeholder="Informe apenas para alterar"
                autoComplete="new-password"
              />
              <label className="inline-flex items-center gap-2 text-xs text-gray-600 mt-2">
                <input
                  type="checkbox"
                  checked={form.clear_api_key}
                  onChange={(e) => setForm((old) => ({ ...old, clear_api_key: e.target.checked, api_key: '' }))}
                />
                Remover API Key atual
              </label>
            </div>
            <div>
              <label className="form-label">Novo Webhook Token (opcional)</label>
              <input
                type="password"
                className="form-input"
                value={form.webhook_token}
                onChange={(e) => setForm((old) => ({ ...old, webhook_token: e.target.value, clear_webhook_token: false }))}
                placeholder="Informe apenas para alterar"
                autoComplete="new-password"
              />
              <label className="inline-flex items-center gap-2 text-xs text-gray-600 mt-2">
                <input
                  type="checkbox"
                  checked={form.clear_webhook_token}
                  onChange={(e) => setForm((old) => ({ ...old, clear_webhook_token: e.target.checked, webhook_token: '' }))}
                />
                Remover webhook token atual
              </label>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.ativo}
                onChange={(e) => setForm((old) => ({ ...old, ativo: e.target.checked }))}
              />
              Integracao Asaas ativa para esta empresa
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button className="btn-primary" onClick={saveAsaas} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar Configuracoes'}
            </button>
            <button
              className="btn-secondary"
              onClick={() => setForm(toFormFromSetting(asaasSetting))}
              disabled={saving}
            >
              Descartar Alteracoes
            </button>
            <p className="text-xs text-gray-500">
              Ultima atualizacao: {formatDateTime(asaasSetting?.updated_at || asaasSetting?.created_at)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SolarSettingsPage;
